// Customer-facing "Sell Your Car" form. Posts to /api/marketplace/my/cars
// which auto-marks the listing as customer_listing + pending_review and
// forces the seller info from the logged-in user record. Photo upload uses
// the matching /my/cars/{id}/photos endpoint (enforces 12-photo max).
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Upload, X, Star, StarOff, Send, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400';

const INITIAL = {
  make: '', model: '', variant: '',
  manufacture_year: new Date().getFullYear() - 2,
  km_driven: '', fuel_type: 'petrol', transmission: 'manual',
  body_type: 'sedan', color: '', owners: 1,
  registration_state: '', asking_price: '',
  city: 'Bengaluru', description: '',
  // Seller fields are filled server-side from the logged-in user.
  seller_name: '', seller_phone: '',
  photos: [], cover_photo: '',
};

const SellYourCar = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const { authHeader, user, loading: authLoading } = useAuth();
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [maxPhotos, setMaxPhotos] = useState(12);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    if (!isEdit) return;
    try {
      const { data } = await axios.get(`${API}/marketplace/my/cars`, { headers: authHeader() });
      const found = (data.items || []).find((c) => c.id === id);
      if (found) {
        const clean = Object.fromEntries(
          Object.entries(found).map(([k, v]) => [k, v == null ? '' : v]),
        );
        setForm({ ...INITIAL, ...clean });
        setMaxPhotos(data.max_photos || 12);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [isEdit, id, authHeader]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav('/login?next=/sell-your-car', { replace: true }); return; }
    load();
  }, [authLoading, user, load, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        manufacture_year: Number(form.manufacture_year),
        km_driven: Number(form.km_driven),
        owners: Number(form.owners) || 1,
        asking_price: Number(form.asking_price),
        // Backend overrides these from the logged-in user — but we send a
        // non-empty value to satisfy server-side validators.
        seller_name: form.seller_name || user.name || 'Owner',
        seller_phone: form.seller_phone || user.phone || '0000000000',
      };
      if (isEdit) {
        await axios.put(`${API}/marketplace/my/cars/${id}`, payload, { headers: authHeader() });
        alert('Saved');
      } else {
        const { data } = await axios.post(`${API}/marketplace/my/cars`, payload, { headers: authHeader() });
        nav(`/sell-your-car/${data.id}`, { replace: true });
        return;
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!isEdit) { alert('Save the listing first, then upload photos.'); return; }
    if (!file) return;
    if ((form.photos || []).length >= maxPhotos) {
      alert(`Photo limit reached (max ${maxPhotos}).`); return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(
        `${API}/marketplace/my/cars/${id}/photos`, fd,
        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } },
      );
      const url = data?.secure_url;
      if (url) {
        setForm((p) => ({ ...p, photos: [...(p.photos || []), url],
                                cover_photo: p.cover_photo || url }));
      }
    } catch (err) { alert(err?.response?.data?.detail || 'Upload failed'); }
  };

  const removePhoto = async (url) => {
    if (!window.confirm('Remove this photo?')) return;
    try {
      await axios.delete(`${API}/marketplace/my/cars/${id}/photos`,
        { headers: authHeader(), params: { url } });
      setForm((p) => ({
        ...p,
        photos: (p.photos || []).filter((x) => x !== url),
        cover_photo: p.cover_photo === url ? ((p.photos || []).find((x) => x !== url) || '') : p.cover_photo,
      }));
    } catch (err) { alert(err?.response?.data?.detail || 'Remove failed'); }
  };

  const submitForReview = async () => {
    if (!isEdit) return;
    if (!(form.photos || []).length) {
      alert('Please upload at least one photo before submitting your listing for review.');
      return;
    }
    try {
      await axios.post(`${API}/marketplace/my/cars/${id}/submit`, {}, { headers: authHeader() });
      alert("Submitted — Unisol's team will review and publish it shortly.");
      nav('/my-listings');
    } catch (err) { alert(err?.response?.data?.detail || 'Submit failed'); }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen bg-slate-50 pt-32 px-6 text-slate-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/my-listings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft size={14} /> My listings
        </Link>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {isEdit ? 'Edit listing' : 'Sell your car'}
        </h1>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
          <ShieldCheck size={14} className="text-teal-600" />
          Unisol verifies every listing before it goes live to buyers.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <Card title="The basics">
            <Grid>
              <Field label="Make *"><input required value={form.make} onChange={(e) => upd('make', e.target.value)} className={inputCls} placeholder="Honda" data-testid="syc-make" /></Field>
              <Field label="Model *"><input required value={form.model} onChange={(e) => upd('model', e.target.value)} className={inputCls} placeholder="City" data-testid="syc-model" /></Field>
              <Field label="Variant"><input value={form.variant} onChange={(e) => upd('variant', e.target.value)} className={inputCls} placeholder="VX MT" /></Field>
              <Field label="Year of manufacture *"><input type="number" required value={form.manufacture_year} onChange={(e) => upd('manufacture_year', e.target.value)} className={inputCls} /></Field>
              <Field label="KM driven *"><input type="number" required value={form.km_driven} onChange={(e) => upd('km_driven', e.target.value)} className={inputCls} placeholder="35000" data-testid="syc-km" /></Field>
              <Field label="Number of owners"><input type="number" min={1} value={form.owners} onChange={(e) => upd('owners', e.target.value)} className={inputCls} /></Field>
            </Grid>
          </Card>

          <Card title="Specifications">
            <Grid>
              <Field label="Fuel *">
                <select value={form.fuel_type} onChange={(e) => upd('fuel_type', e.target.value)} className={inputCls}>
                  {['petrol', 'diesel', 'cng', 'electric', 'hybrid'].map((o) => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
                </select>
              </Field>
              <Field label="Transmission *">
                <select value={form.transmission} onChange={(e) => upd('transmission', e.target.value)} className={inputCls}>
                  {['manual', 'automatic', 'amt', 'cvt', 'dct'].map((o) => <option key={o} value={o}>{o.toUpperCase()}</option>)}
                </select>
              </Field>
              <Field label="Body type">
                <select value={form.body_type} onChange={(e) => upd('body_type', e.target.value)} className={inputCls}>
                  {['hatchback', 'sedan', 'suv', 'muv', 'coupe', 'convertible', 'pickup', 'van', 'wagon'].map((o) =>
                    <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
                </select>
              </Field>
              <Field label="Colour"><input value={form.color} onChange={(e) => upd('color', e.target.value)} className={inputCls} placeholder="Pearl White" /></Field>
              <Field label="RTO state"><input value={form.registration_state} onChange={(e) => upd('registration_state', e.target.value)} className={inputCls} placeholder="KA" maxLength={3} /></Field>
              <Field label="City"><input value={form.city} onChange={(e) => upd('city', e.target.value)} className={inputCls} /></Field>
            </Grid>
          </Card>

          <Card title="Price & details">
            <Grid>
              <Field label="Asking price (₹) *">
                <input type="number" required value={form.asking_price} onChange={(e) => upd('asking_price', e.target.value)} className={inputCls} placeholder="550000" data-testid="syc-price" />
              </Field>
            </Grid>
            <label className="block mt-4">
              <span className="block text-xs font-medium text-slate-600 mb-1">Description</span>
              <textarea rows={4} value={form.description} onChange={(e) => upd('description', e.target.value)}
                className={inputCls + ' resize-y'}
                placeholder="Single owner, full service history, garage-kept…" />
            </label>
          </Card>

          <Card title={`Photos (max ${maxPhotos})`}>
            {!isEdit ? (
              <p className="text-sm text-slate-500">Save the basics first — then add photos here. You need at least one photo to submit your listing for review.</p>
            ) : (
              <>
                <label className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer" data-testid="syc-upload">
                  <Upload size={14} /> Upload photo
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ''; }} />
                </label>
                <p className="text-xs text-slate-500 mt-2">
                  {(form.photos || []).length}/{maxPhotos} uploaded. Click the star icon to pick the cover.
                </p>
                {(form.photos || []).length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
                    {form.photos.map((p) => (
                      <div key={p} className={`relative group rounded-lg overflow-hidden border-2 ${form.cover_photo === p ? 'border-teal-500' : 'border-transparent'}`}>
                        <img src={p} alt="" className="w-full h-32 object-cover bg-slate-100" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          <button type="button" onClick={() => upd('cover_photo', p)} className="bg-white text-slate-900 p-2 rounded-full" title="Set as cover">
                            {form.cover_photo === p ? <Star size={14} className="text-amber-500 fill-amber-500" /> : <StarOff size={14} />}
                          </button>
                          <button type="button" onClick={() => removePhoto(p)} className="bg-rose-600 text-white p-2 rounded-full" title="Remove"><X size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>

          <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
            <button type="submit" disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-xl text-sm inline-flex items-center gap-2"
              data-testid="syc-save">
              <Save size={14} /> {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Save & add photos')}
            </button>
            {isEdit && (
              <button type="button" onClick={submitForReview}
                className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold px-6 py-3 rounded-xl text-sm inline-flex items-center gap-2"
                data-testid="syc-submit-review">
                <Send size={14} /> Submit for review
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-4">{title}</h2>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
    {children}
  </label>
);

export default SellYourCar;
